/**
 * Settings → แผนงบ เป้า และนิยาม Result. The Result definition section comes first (DEC3). Composition and role resolution only; the sections
 * own their tables. The master is synthesised as `admin` in workspace.userRole,
 * so the master is told apart by useIsPlatformMaster — and nothing role-bound
 * renders until that answer is in. A failed read says so: never an empty table.
 */
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useIsPlatformMaster } from '@/hooks/useIsPlatformMaster';
import { useResultMappings } from '@/hooks/useResultMappings';
import { useWorkspaceTargets } from '@/hooks/useWorkspaceTargets';
import { todayDataDate } from '@/services/dataDate';
import type { WorkspaceTargets } from '@/services/targets/targetsService';
import { ResultMappingSection } from './ResultMappingSection';
import { BudgetPlanSection } from './BudgetPlanSection';
import { CprTargetSection } from './CprTargetSection';
import { PendingProposals } from './PendingProposals';
import { TargetHistory } from './TargetHistory';
import { mostCommon, type TargetRole } from './targetUi';

const FALLBACK_CURRENCY = 'THB';
const OTHER = '__other';

/** The currencies already in use, approved ones counted for the default. */
const currenciesOf = (data: WorkspaceTargets): { known: string[]; preferred: string } => {
  const all = [...data.plans, ...data.targets];
  const approved = all.filter((r) => r.status === 'approved').map((r) => r.currency);
  const known = [...new Set([FALLBACK_CURRENCY, ...all.map((r) => r.currency)])];
  return { known, preferred: mostCommon(approved, FALLBACK_CURRENCY) };
};

const CURRENCY_CODE = /^[A-Z]{3}$/;

/** `draft` null = picking from the list; a string = typing a new code under "Other". */
interface Picked { code: string; draft: string | null }
interface PickerProps { known: string[]; value: string; draft: string | null; onPick: (next: Picked) => void }

/**
 * Which currency the tables show and the editor writes. A new code may be
 * typed under "Other"; the current currency stays in force until the draft is
 * 3 letters, so the tables never go blank mid-typing.
 */
const CurrencyPicker: React.FC<PickerProps> = ({ known, value, draft, onPick }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <label className="flex items-center gap-2 text-neutral-700">
        {t('targets.currency')}
        <select className="rounded border border-neutral-300 px-2 py-1 text-ink-900" value={draft !== null ? OTHER : value}
          onChange={(e) => onPick(e.target.value === OTHER ? { code: value, draft: '' } : { code: e.target.value, draft: null })}>
          {known.map((c) => <option key={c} value={c}>{c}</option>)}
          <option value={OTHER}>{t('targets.currencyOther')}</option>
        </select>
      </label>
      {draft !== null && (
        <input type="text" maxLength={3} aria-label={t('targets.currencyCode')} placeholder="USD"
          className="w-20 rounded border border-neutral-300 px-2 py-1 uppercase" value={draft}
          onChange={(e) => {
            const next = e.target.value.toUpperCase();
            onPick({ code: CURRENCY_CODE.test(next) ? next : value, draft: next });
          }} />
      )}
    </div>
  );
};

export const TargetsPanel: React.FC<{ workspaceId: string }> = ({ workspaceId }) => {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { workspace } = useWorkspace();
  const { isMaster, loading: masterLoading } = useIsPlatformMaster(!!session);
  const { state, reload: reloadTargets } = useWorkspaceTargets(workspaceId);
  const { state: mapping, reload: reloadMapping } = useResultMappings(workspaceId);
  const [picked, setPicked] = useState<Picked | null>(null);
  // Every retry path re-reads BOTH: after another admin switches the mapping a targets-only reload stays `stale`.
  const reload = useCallback(() => { reloadTargets(); reloadMapping(); }, [reloadTargets, reloadMapping]);

  if (masterLoading || state.status === 'loading') {
    return <p className="text-sm text-neutral-700">{t('common.loading')}</p>;
  }
  if (state.status === 'failed') {
    return (
      <div className="bg-white rounded-xl border border-error-200 p-6">
        <p role="alert" className="text-sm text-error-700">{t('targets.loadFailed')}</p>
        <button type="button" className="mt-2 text-xs font-medium underline" onClick={reload}>{t('targets.reload')}</button>
      </div>
    );
  }

  const role: TargetRole = isMaster ? 'master' : (workspace?.userRole ?? 'viewer');
  const me = session?.user.id ?? null;
  const today = todayDataDate();
  const { known, preferred } = currenciesOf(state.data);
  const currency = picked?.code ?? preferred;
  const shared = { workspaceId, currency, today, role, me, reload };

  return (
    <div className="space-y-6">
      <ResultMappingSection workspaceId={workspaceId} role={role} me={me} mapping={mapping} targets={state.data.targets}
        onSaved={reload} reloadMapping={reloadMapping} />
      {(role === 'admin' || role === 'master') && (
        <PendingProposals plans={state.data.plans} targets={state.data.targets} me={me} today={today} reload={reload} />
      )}
      <CurrencyPicker known={known} value={currency} draft={picked?.draft ?? null} onPick={setPicked} />
      <BudgetPlanSection {...shared} plans={state.data.plans} />
      {mapping.status === 'ready' && <CprTargetSection {...shared} targets={state.data.targets} awareness={mapping.awareness} />}
      {mapping.status === 'loading' && <p className="text-sm text-neutral-700">{t('common.loading')}</p>}
      {mapping.status === 'failed' && (
        // R15: never an unfiltered table — the targets cannot be matched to a definition we could not read.
        <div className="bg-white rounded-xl border border-error-200 p-6">
          <p role="alert" className="text-sm text-error-700">{t('targets.mapping.loadFailed')}</p>
          <button type="button" className="mt-2 text-xs font-medium underline" onClick={reloadMapping}>{t('targets.reload')}</button>
        </div>
      )}
      <TargetHistory plans={state.data.plans} targets={state.data.targets} me={me} />
    </div>
  );
};

export default TargetsPanel;
